import React, { ComponentProps, useRef, useState } from 'react';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import { asFetchableView, useRefreshView } from '../../../_lib/ViewFetcher';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { DraggableExternalLinkListItem } from '../partials/ExternalLinkListItem';
import { SortableContentList } from '../../../_lib/components/SortableContentList';
import { useArrayEffect } from '../../../_lib/components/SortableContentList/SortableContentList';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ExternalLinksAddOrUpdateForm, ExtrenalLinksForm } from '../partials/ExternalLinksAddOrUpdateForm';
import { ExternalLinksDeleteForm } from '../partials/ExternalLinksDeleteForm';
import { ExternalLinksEditableElement } from '../ExternalLinksTypes';
import { ExternalLinksEditHeadlineForm } from '../partials/ExternalLinksEditHeadlineForm';
import { SonForm, TextParam } from '../../../_lib/Form2';

type ExternalLinksEditableListProps = {
  items: ExternalLinksEditableElement[];
  create: ExtrenalLinksForm;
  updateOrder?: ExtrenalLinksForm;
  updateBlock?: SonForm<
      | TextParam<'title'>
  >;
};

export function ExternalLinksEdit({ items, create, updateOrder, updateBlock }: ExternalLinksEditableListProps) {
  const { t } = useSonTranslation('ExternalLinks');
  const ref = useRef<HTMLButtonElement>(null);

  const { delayedRefreshView } = useRefreshView();

  const [activeForm, setActiveForm] = useState<{ type: 'add' | 'edit' | 'delete'; form: ExtrenalLinksForm } | null>(
    null,
  );

  /**
   * After successfully changes, modal should be closed
   * */
  useArrayEffect(() => setActiveForm(null), items);

  const closeModal = () => setActiveForm(null);
  const openAddForm = () => setActiveForm({ type: 'add', form: create });
  const openEditForm = (form: ExtrenalLinksForm) => setActiveForm({ type: 'edit', form });
  const openDeleteForm = (form: ExtrenalLinksForm) => setActiveForm({ type: 'delete', form });

  const switchActiveForm = () => {
    switch (activeForm?.type) {
      case 'delete':
        return (
          <ModalDialog closeModal={closeModal} headline={t('removeLink')} focusOnReturn={ref} initialFocusIndex={1}>
            <ExternalLinksDeleteForm form={activeForm.form} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      case 'add':
        return (
          <ModalDialog closeModal={closeModal} headline={t('addLink')} initialFocusIndex={1}>
            <ExternalLinksAddOrUpdateForm form={activeForm.form} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      case 'edit':
        return (
          <ModalDialog closeModal={closeModal} headline={t('editLink')} initialFocusIndex={1}>
            <ExternalLinksAddOrUpdateForm form={activeForm.form} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {updateBlock && <ExternalLinksEditHeadlineForm form={updateBlock} />}
      <BlockBar
        heading={t('addLink')}
        hierarchy={3}
        modifier={items.length > 0 ? ['border-bottom', 'border-top'] : 'border-top'}
      >
        <BlockBarButton ref={ref} onClick={openAddForm} text={t('addLink')} svgSpriteId={Icons.ADD_PLUS} />
        <Heading hierarchy={4} className="invisible">
          {t('existingLinks')}:
        </Heading>
      </BlockBar>
      <SortableContentList items={items} form={updateOrder}>
        {(link: ExternalLinksEditableElement) => (
          <DraggableExternalLinkListItem
            key={link.id}
            {...link}
            openDeleteForm={openDeleteForm}
            openEditForm={openEditForm}
          />
        )}
      </SortableContentList>
      {switchActiveForm()}
    </>
  );
}

export const ExternalLinksEditView = asFetchableView(ExternalLinksEdit);
export type ExternalLinksEditView = ComponentProps<typeof ExternalLinksEditView>;
